import {CSSProperties, memo, useMemo} from "react";
import {CELL_SIZE} from "../../utility/types";
import {Vec2} from "../../utility/math/vec2";
import {PopoverRoot, PopoverTrigger, PopoverContent, PopoverBody} from "../../../../../../../../components/ui/popover";
import {Badge, Box, HStack, Stack, Text} from "@chakra-ui/react";
import type {MockSourceEvidence} from "../../../../types";

interface NodeBadgeProps {
  position: Vec2;
  citations: number[];
  evidence: MockSourceEvidence[];
}

export const NodeBadge = memo(({position, citations, evidence}: NodeBadgeProps) => {
  const worldPos = position.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE);

  const badgeStyle = useMemo<CSSProperties>(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      // Position badge at top-right corner of node (offset +28px x, -28px y from center)
      transform: `translate(${worldPos.x + 28}px, ${worldPos.y - 28}px) translate(-50%, -50%)`,
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      backgroundColor: "var(--chakra3-colors-blue-500)",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "bold",
      cursor: "pointer",
      zIndex: 10,
      border: "2px solid white",
      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
    }),
    [worldPos.x, worldPos.y],
  );

  const hoverStyle = useMemo<CSSProperties>(
    () => ({...badgeStyle, backgroundColor: "var(--chakra3-colors-blue-600)"}),
    [badgeStyle],
  );

  // Get extractions for these citation numbers
  const extractions = useMemo(() => {
    const allExtractions = evidence.flatMap(e =>
      e.extractions.map(ext => ({...ext, sourceName: e.source_name, sourceId: e.source_id})),
    );
    return allExtractions.filter(ext => citations.includes(ext.citation_number));
  }, [citations, evidence]);

  return (
    <PopoverRoot positioning={{placement: "top"}}>
      <PopoverTrigger asChild>
        <div
          style={badgeStyle}
          onMouseEnter={e => {
            Object.assign(e.currentTarget.style, hoverStyle);
          }}
          onMouseLeave={e => {
            Object.assign(e.currentTarget.style, badgeStyle);
          }}
        >
          {citations.length}
        </div>
      </PopoverTrigger>
      <PopoverContent maxW="md">
        <PopoverBody>
          <Stack gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="medium">
                Supporting Evidence
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {citations.length} {citations.length === 1 ? "citation" : "citations"} for this node
              </Text>
            </Box>
            <Stack gap={2}>
              {extractions.map(extraction => (
                <Box
                  key={extraction.citation_number}
                  bg="bg.subtle"
                  p={3}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.subtle"
                >
                  <HStack gap={2} mb={1}>
                    <Badge colorPalette="blue" size="sm">
                      {extraction.citation_number}
                    </Badge>
                    <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                      {extraction.sourceName}
                    </Text>
                  </HStack>
                  {extraction.page_reference && (
                    <Text fontSize="xs" color="fg.muted" mb={1}>
                      {extraction.page_reference}
                    </Text>
                  )}
                  <Text fontSize="sm">{extraction.quote}</Text>
                </Box>
              ))}
            </Stack>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
});
